import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import { NotificationService } from "ui-components/notification";

import { styles } from "./create-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";
import { WowFormController } from "ui-common/form/wow-form-controller";
import type { CreateLiveEventParams } from "api-contracts/rpc/create-live-event";
import { t, translate } from "../../translations/i18n";
import {
  createLiveEventValidators,
  MAX_ATTENDEES_LIMIT,
  MAX_PRICE_LIMIT,
} from "ui-common/form/validators/live-event-validators";
import type { LiveEventFormData } from "ui-common/form/validators/live-event-validators";

@customElement("create-live-event-modal")
export class CreateLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private backgroundImageFileIds: string[] = [];

  @state()
  private backgroundImageUrls: string[] = [];

  @state()
  private isSubmitting = false;

  @state()
  private errors: Record<string, string> = {};

  private formController!: WowFormController<LiveEventFormData>;
  private hasInitializedContentTier = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.initializeFormController();
  }

  private initializeFormController(): void {
    const validators = createLiveEventValidators(translate as (key: string) => string);

    this.formController = new WowFormController<LiveEventFormData>(
      this,
      {
        title: "",
        description: "",
        feeEuros: "",
        scheduledDate: "",
        scheduledTime: "",
        maxAttendees: "",
        whatsappUrl: "",
        ctaText: "",
      },
      {
        validateOnBlur: true,
        validators: {
          title: [validators.validateTitle],
          description: [validators.validateDescription],
          feeEuros: [validators.createFeeValidator(() => this.contentTier)],
          scheduledDate: [validators.validateDate],
          scheduledTime: [validators.validateTime],
          maxAttendees: [validators.validateMaxAttendees],
        },
      },
      async (event) => {
        if (!event.isValid || !event.data) return;
        await this.submitForm(event.data);
      },
      "#live-event-form",
    );
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("featureFlags") && !this.hasInitializedContentTier && this.featureFlags.length > 0) {
      this.contentTier = this.defaultContentTier;
      this.hasInitializedContentTier = true;
    }
  }

  private async submitForm(data: LiveEventFormData): Promise<void> {
    const scheduledDate = data.scheduledDate;
    const scheduledTime = data.scheduledTime;

    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);

    if (isNaN(scheduledDateTime.getTime())) {
      NotificationService.error("Invalid date or time format");
      return;
    }

    if (scheduledDateTime <= new Date()) {
      this.errors = { datetime: translate("createLiveEventsModal.dateMustBeFuture") };
      return;
    }

    this.isSubmitting = true;

    try {
      const localDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const params: CreateLiveEventParams = {
        title: data.title.trim(),
        description: data.description.trim(),
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" ? Math.round(parseFloat(data.feeEuros) * 100) : undefined,
        scheduled_start_time: isoString,
        max_attendees: data.maxAttendees ? Number(data.maxAttendees) : undefined,
        thumbnail_file_id: this.thumbnailFileId,
        whatsapp_url: data.whatsappUrl?.trim() || undefined,
        post_buying_video_file_id: this.postBuyingVideoFileId,
        cta_text: data.ctaText?.trim() || undefined,
        background_image_file_ids: this.backgroundImageFileIds.length > 0 ? this.backgroundImageFileIds : undefined,
      };

      const result = await rpc("create-live-event", params);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success("Live event created successfully!");

        this.dispatchEvent(
          new CustomEvent("event-created", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay to allow notification to be seen
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || "Failed to create live event"
            : "Failed to create live event";
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error("Failed to create live event");
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleSubmit(): void {
    this.formController.submit();
  }

  private handleClose() {
    // Reset form when closing
    this.resetForm();
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private get hasBothLiveEventFlags(): boolean {
    return this.featureFlags.includes("open-live-events") && this.featureFlags.includes("paid-live-events");
  }

  private get hasBackgroundImagesFlag(): boolean {
    return this.featureFlags.includes("live-event-background-images");
  }

  private get defaultContentTier(): "free" | "premium" {
    if (this.featureFlags.includes("paid-live-events")) {
      return "premium";
    }
    return "free";
  }

  private get todayDate(): string {
    return new Date().toISOString().split("T")[0]!;
  }

  private resetForm() {
    this.formController.reset();
    this.contentTier = this.defaultContentTier;
    this.thumbnailFileId = undefined;
    this.thumbnailUrl = undefined;
    this.postBuyingVideoUrl = "";
    this.postBuyingVideoFileId = undefined;
    this.backgroundImageFileIds = [];
    this.backgroundImageUrls = [];
    this.isSubmitting = false;
    this.errors = {};
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  private handleBackgroundImageUpload(index: number, e: CustomEvent) {
    const { fileId, fileUrl } = e.detail;
    const newFileIds = [...this.backgroundImageFileIds];
    const newUrls = [...this.backgroundImageUrls];

    if (fileId) {
      newFileIds[index] = fileId;
      newUrls[index] = fileUrl || "";
    } else {
      newFileIds.splice(index, 1);
      newUrls.splice(index, 1);
    }

    this.backgroundImageFileIds = newFileIds.filter(Boolean);
    this.backgroundImageUrls = newUrls.filter(Boolean);
  }

  protected override render() {
    const submitText = this.isSubmitting
      ? t("createLiveEventsModal.creating")
      : t("createLiveEventsModal.submitButton");

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">${t("createLiveEventsModal.header")}</span>

        <form id="live-event-form" class="form-content">
          <!-- Event Cover Image -->
          <media-uploader
            responsive
            context="cover_image"
            label="${t("createLiveEventsModal.firstThumb")}"
            subtitle="${t("createLiveEventsModal.firstThumbSubtitle")}"
            viewport="cover"
            currentFileUrl=${this.thumbnailUrl}
            @change=${this.handleThumbnailUpload}
          ></media-uploader>

          <!-- Title -->
          <wow-input
            name=${this.formController.fieldNames.title}
            label="${t("createLiveEventsModal.title")}"
            placeholder="${t("createLiveEventsModal.titlePlaceholder")}"
            help-text=${t("createLiveEventsModal.titleHelper")}
            required
          ></wow-input>

          <!-- Description -->
          <wow-textarea
            name=${this.formController.fieldNames.description}
            label="${t("createLiveEventsModal.description")}"
            placeholder="${t("createLiveEventsModal.descriptionPlaceholder")}"
            help-text=${t("createLiveEventsModal.descriptionHelper")}
            rows="3"
            maxlength="750"
            autogrow
            required
          ></wow-textarea>

          <!-- Access Level - only show if user has both flags -->
          ${this.hasBothLiveEventFlags
            ? html`
                <wow-select
                  label="${t("createLiveEventsModal.accessLevel")}"
                  help-text=${t("createLiveEventsModal.accessLevelHelper")}
                  .options=${[
                    { value: "free", label: "Free - Anyone can attend" },
                    { value: "premium", label: "Premium - Ticket required" },
                  ]}
                  .value=${this.contentTier}
                  @change=${(e: CustomEvent) => {
                    this.contentTier = e.detail.value;
                    this.errors = { ...this.errors, fee: "" };
                  }}
                ></wow-select>
              `
            : nothing}

          <!-- Ticket Price - only show for premium events -->
          ${this.contentTier === "premium"
            ? html`
                <wow-input
                  name=${this.formController.fieldNames.feeEuros}
                  type="number"
                  inputmode="decimal"
                  label="${t("createLiveEventsModal.price")}"
                  placeholder="${t("createLiveEventsModal.pricePlaceholder")}"
                  help-text=${t("createLiveEventsModal.priceHelper")}
                  min="0.50"
                  max="${MAX_PRICE_LIMIT}"
                  step="0.01"
                  required
                ></wow-input>
              `
            : nothing}

          <!-- Date and Time -->
          <div class="form-row">
            <wow-input
              name=${this.formController.fieldNames.scheduledDate}
              type="date"
              label="${t("createLiveEventsModal.date")}"
              min=${this.todayDate}
              @input=${() => {
                this.errors = { ...this.errors, datetime: "" };
              }}
              help-text=${t("createLiveEventsModal.dateHelper")}
              required
            ></wow-input>

            <wow-input
              name=${this.formController.fieldNames.scheduledTime}
              type="time"
              label="${t("createLiveEventsModal.time")}"
              @input=${() => {
                this.errors = { ...this.errors, datetime: "" };
              }}
              help-text=${t("createLiveEventsModal.dateHelper")}
              required
            ></wow-input>
          </div>

          <!-- Max Attendees -->
          <wow-input
            name=${this.formController.fieldNames.maxAttendees}
            type="number"
            inputmode="numeric"
            label="${t("createLiveEventsModal.maxAttendees")}"
            placeholder="${t("createLiveEventsModal.maxAttendeesPlaceholder")}"
            help-text=${t("createLiveEventsModal.maxAttendeesHelper")}
            step="1"
            min="1"
            max="${MAX_ATTENDEES_LIMIT}"
            @keydown=${(e: KeyboardEvent) => {
              if (
                !/[0-9]/.test(e.key) &&
                e.key !== "Backspace" &&
                e.key !== "Delete" &&
                e.key !== "ArrowLeft" &&
                e.key !== "ArrowRight" &&
                e.key !== "Tab"
              ) {
                e.preventDefault();
              }
            }}
          ></wow-input>

          <!-- WhatsApp URL -->
          <wow-input
            name=${this.formController.fieldNames.whatsappUrl}
            type="url"
            label="${t("createLiveEventsModal.whatsApp")}"
            placeholder="https://chat.whatsapp.com/..."
            help-text=${t("createLiveEventsModal.whatsAppHelper")}
          ></wow-input>

          <!-- Post-Purchase Media -->
          <media-uploader
            responsive
            context="cover_image"
            label="${t("createLiveEventsModal.secondThumb")}"
            subtitle=${t("createLiveEventsModal.secondThumbSubtitle")}
            viewport="post"
            currentFileUrl=${this.postBuyingVideoUrl}
            @change=${this.handleVideoUpload}
          ></media-uploader>

          <!-- CTA Text -->
          <wow-input
            name=${this.formController.fieldNames.ctaText}
            label="${t("createLiveEventsModal.ctaButton")}"
            placeholder="${t("createLiveEventsModal.ctaButtonPlaceholder")}"
            maxlength="255"
          ></wow-input>

          ${this.hasBackgroundImagesFlag
            ? html`
                <div class="background-images-section">
                  <label class="section-label">${t("createLiveEventsModal.bgImage")}</label>
                  <div class="background-images-grid">
                    ${[0, 1, 2, 3].map(
                      (index) => html`
                        <media-uploader
                          context="cover_image"
                          viewport="post"
                          currentFileUrl=${this.backgroundImageUrls[index] || ""}
                          @change=${(e: CustomEvent) => this.handleBackgroundImageUpload(index, e)}
                        ></media-uploader>
                      `,
                    )}
                  </div>
                </div>
              `
            : nothing}
        </form>

        <span slot="footer-button-text">${submitText}</span>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "create-live-event-modal": CreateLiveEventModal;
  }
}
